/**
 * Entry point for the standalone widget bundle. Exposes window.FrsLeadForms and:
 *  - auto-mounts inline widgets on [data-frs-llf-widget] containers
 *  - wires [data-frs-llf-popup] triggers to a full-screen modal
 *  - reads shared defaults from window.frsLLF (localized by the WP plugin)
 *
 * Data attributes (container or trigger):
 *   data-loan-type="purchase|refinance|ask"   (default: ask)
 *   data-primary-color, data-accent-color      (optional brand overrides)
 */
import { FrsLeadWidget } from './widget';
import { OVERLAY_CSS } from './styles';
import { icon } from './icons';
import type { LoanType, WidgetConfig } from './types';

interface GlobalDefaults {
  restUrl?: string;
  nonce?: string;
  brand?: WidgetConfig['brand'];
  loanOfficers?: WidgetConfig['loanOfficers'];
  meta?: Record<string, string>;
}

function defaults(): GlobalDefaults {
  return (window as unknown as { frsLLF?: GlobalDefaults }).frsLLF || {};
}

function readConfig(source: HTMLElement, override: Partial<WidgetConfig> = {}): WidgetConfig {
  const d = defaults();
  const ds = source.dataset;
  const loanType = (ds.loanType as LoanType | 'ask') || override.loanType || 'ask';
  const brand = { ...(d.brand || {}) };
  if (ds.primaryColor) brand.primaryColor = ds.primaryColor;
  if (ds.accentColor) brand.accentColor = ds.accentColor;
  const restUrl = d.restUrl || override.restUrl || '';
  if (!restUrl) console.warn('[frs-llf] No REST URL configured; submissions will fail.');
  return {
    loanType,
    restUrl,
    nonce: d.nonce,
    brand,
    loanOfficers: d.loanOfficers || [],
    meta: d.meta,
    ...override,
  };
}

/** Mount an inline widget into a container element. */
export function mount(el: HTMLElement, cfg?: Partial<WidgetConfig>): FrsLeadWidget {
  if (el.dataset.frsLlfMounted === '1') return (el as unknown as { _frs: FrsLeadWidget })._frs;
  el.dataset.frsLlfMounted = '1';
  const widget = new FrsLeadWidget(el, readConfig(el, cfg));
  (el as unknown as { _frs: FrsLeadWidget })._frs = widget;
  return widget;
}

let overlayStyleInjected = false;
let openOverlay: HTMLElement | null = null;

function injectOverlayStyle(): void {
  if (overlayStyleInjected) return;
  const s = document.createElement('style');
  s.id = 'frs-llf-overlay-style';
  s.textContent = OVERLAY_CSS;
  document.head.appendChild(s);
  overlayStyleInjected = true;
}

/** Open the widget in a full-screen popup overlay. */
export function open(cfg?: Partial<WidgetConfig>, source?: HTMLElement): void {
  if (openOverlay) close();
  injectOverlayStyle();
  const overlay = document.createElement('div');
  overlay.className = 'frs-llf-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  const inner = document.createElement('div');
  inner.className = 'frs-llf-overlay-inner';
  const closeBtn = document.createElement('button');
  closeBtn.className = 'frs-llf-overlay-close';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.innerHTML = icon('x');
  closeBtn.addEventListener('click', close);
  const holder = document.createElement('div');
  inner.appendChild(closeBtn);
  inner.appendChild(holder);
  overlay.appendChild(inner);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  document.body.appendChild(overlay);
  document.documentElement.classList.add('frs-llf-locked');
  document.body.classList.add('frs-llf-locked');
  openOverlay = overlay;
  document.addEventListener('keydown', onEsc);
  new FrsLeadWidget(holder, readConfig(source || holder, cfg));
}

export function close(): void {
  if (!openOverlay) return;
  openOverlay.remove();
  openOverlay = null;
  document.documentElement.classList.remove('frs-llf-locked');
  document.body.classList.remove('frs-llf-locked');
  document.removeEventListener('keydown', onEsc);
}

function onEsc(e: KeyboardEvent): void {
  if (e.key === 'Escape') close();
}

/**
 * Map a URL hash to a loan type, so a plain link can open the popup:
 *   #frs-apply            -> ask (Purchase/Refinance chooser)
 *   #frs-apply-purchase   -> purchase
 *   #frs-apply-refinance  -> refinance
 */
const APPLY_HASH = /^#frs-apply(?:-(purchase|refinance))?$/i;

function loanTypeFromHash(hash: string): LoanType | 'ask' | null {
  const m = APPLY_HASH.exec(hash || '');
  if (!m) return null;
  return (m[1] as LoanType) || 'ask';
}

function maybeOpenFromHash(): void {
  const lt = loanTypeFromHash(location.hash);
  if (lt) open({ loanType: lt });
}

let hashWired = false;
/** Any link (e.g. a WordPress Button) whose URL is #frs-apply opens the popup. */
function wireHashTriggers(): void {
  if (hashWired) return;
  hashWired = true;
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement | null;
    const a = target && target.closest ? (target.closest('a[href*="#frs-apply"]') as HTMLAnchorElement | null) : null;
    if (!a) return;
    const href = a.getAttribute('href') || '';
    const lt = loanTypeFromHash(href.slice(href.indexOf('#')));
    if (!lt) return;
    e.preventDefault();
    open({ loanType: lt }, a);
  });
  window.addEventListener('hashchange', maybeOpenFromHash);
  maybeOpenFromHash(); // deep-link: page loaded already pointing at #frs-apply
}

/** Scan the DOM for inline containers and popup triggers, wiring all three. */
export function init(): void {
  document.querySelectorAll<HTMLElement>('[data-frs-llf-widget]').forEach((el) => mount(el));
  document.querySelectorAll<HTMLElement>('[data-frs-llf-popup]').forEach((el) => {
    if (el.dataset.frsLlfBound === '1') return;
    el.dataset.frsLlfBound = '1';
    el.addEventListener('click', (e) => {
      e.preventDefault();
      const loanType = (el.dataset.loanType as LoanType | 'ask') || 'ask';
      open({ loanType }, el);
    });
  });
  wireHashTriggers();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
